import React, {useEffect, useMemo, useState} from 'react';

import {apiGetFdApplication, apiRunItcTraderApplication} from 'constants/Api';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import {ErrorBanner, FormField, TextInput} from './shared/components';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';

const CREDIT_PROGRESS_STEP = 3;
const MAX_CREDIT_SCORE = 850;

function lshClamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function lshGetCreditScoreLabel(score: number): string {
  if (score < 300) return 'Very Poor';
  if (score < 580) return 'Poor';
  if (score < 670) return 'Fair';
  if (score < 740) return 'Good';
  if (score < 800) return 'Very Good';
  return 'Excellent';
}

function lshGetCreditScoreMessage(label: string): string {
  if (label === 'Very Poor') {
    return 'Your score is quite low. Consider improving your credit to get better funding options.';
  }

  if (label === 'Poor') {
    return 'Your score is still below ideal. You may face higher interest rates.';
  }

  if (label === 'Fair') {
    return 'Your credit score is fair, but there is room for improvement.';
  }

  if (label === 'Good') {
    return 'You have a good credit score. You can likely get decent rates.';
  }

  if (label === 'Very Good') {
    return 'Your credit score is very good! You’ll receive favorable rates.';
  }

  return 'Your credit score is excellent! You have great funding options available.';
}

function lshFormatCreditScore(score: number | null | undefined): {
  displayScore: string;
  label: string;
  message: string;
  indicatorPosition: string;
} {
  const numericScore =
    typeof score === 'number' && !Number.isNaN(score) ? score : null;
  const displayScore =
    numericScore !== null ? String(Math.round(numericScore)) : '—';
  const label =
    numericScore !== null
      ? lshGetCreditScoreLabel(numericScore)
      : 'Unavailable';
  const message =
    numericScore !== null
      ? lshGetCreditScoreMessage(label)
      : "We couldn't retrieve your score yet. Try running a credit check.";
  const indicatorPosition =
    numericScore !== null
      ? `${lshClamp((numericScore / MAX_CREDIT_SCORE) * 100, 0, 100)}%`
      : '0%';

  return {
    displayScore,
    label,
    message,
    indicatorPosition,
  };
}

const ApplicationCreditCheckStep: React.FC = () => {
  const {
    applicationId,
    application,
    applicantSummary,
    goPrevious,
    goNext,
    setApplication,
  } = useSupplierApplicationJourney();

  const [idNumber, setIdNumber] = useState(applicantSummary.idNumber);
  const [hasCompletedCreditCheck, setHasCompletedCreditCheck] = useState(
    applicantSummary.creditScore !== null && applicantSummary.creditScore >= 0,
  );
  const [isRunningCreditCheck, setIsRunningCreditCheck] = useState(false);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (applicantSummary.idNumber) {
      setIdNumber(applicantSummary.idNumber);
    }
  }, [applicantSummary.idNumber]);

  useEffect(() => {
    if (applicantSummary.creditScore !== null) {
      if (applicantSummary.creditScore < 0) {
        setHasCompletedCreditCheck(false);
        setErrorMessage(
          'The ID number provided is incorrect. Please check the ID number and try again.',
        );
      } else {
        setHasCompletedCreditCheck(true);
        setErrorMessage(null);
      }
    }
  }, [applicantSummary.creditScore]);

  const creditScoreCard = useMemo(
    () => lshFormatCreditScore(applicantSummary.creditScore),
    [applicantSummary.creditScore],
  );

  const isBusy = isRunningCreditCheck || isUpdatingProgress;
  const shouldShowScoreCard = hasCompletedCreditCheck;
  const primaryActionLabel = isUpdatingProgress
    ? 'Saving...'
    : shouldShowScoreCard
    ? 'Next'
    : isRunningCreditCheck
    ? 'Running credit check...'
    : 'Run credit check';

  const handleRunCreditCheck = async () => {
    if (isBusy) return;
    if (!idNumber.trim()) {
      setErrorMessage('Please enter your ID number first.');
      return;
    }

    const applicantName = String(application?.applyingUser?.name ?? '').trim();
    const applicantSurname = String(
      application?.applyingUser?.surname ?? '',
    ).trim();

    if (!applicantName || !applicantSurname) {
      setErrorMessage(
        'We could not find the applicant name and surname. Please refresh the application and try again.',
      );
      return;
    }

    setIsRunningCreditCheck(true);
    setErrorMessage(null);

    try {
      await apiRunItcTraderApplication({
        name: applicantName,
        surname: applicantSurname,
        idNumber: idNumber.trim(),
        externalServiceRequest: true,
      });

      const refreshedApplication = await apiGetFdApplication(
        Number(application?.id || applicationId),
      );

      let nextScore: number | null = null;
      if (refreshedApplication) {
        setApplication(refreshedApplication as any);
        const scoreVal = refreshedApplication.applyingUser?.creditScore;
        if (scoreVal !== undefined && scoreVal !== null) {
          nextScore = Number(scoreVal);
        }
      }

      if (nextScore !== null && nextScore < 0) {
        setErrorMessage(
          'The ID number provided is incorrect. Please check the ID number and try again.',
        );
        setHasCompletedCreditCheck(false);
      } else {
        setHasCompletedCreditCheck(true);
      }
    } catch (error: any) {
      setErrorMessage(
        error?.message ||
          'We could not complete the credit check. Please try again.',
      );
    } finally {
      setIsRunningCreditCheck(false);
    }
  };

  const handleProgressAdvance = () => {
    if (isBusy) return;

    setIsUpdatingProgress(true);
    setErrorMessage(null);

    try {
      setApplication((current) => {
        if (!current) return current;

        return {
          ...current,
          progress: Math.max(
            Number(current.progress || 0),
            CREDIT_PROGRESS_STEP,
          ),
          subProgress: 0,
        };
      });

      goNext();
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const handlePrimaryAction = () => {
    if (shouldShowScoreCard) {
      handleProgressAdvance();
      return;
    }

    void handleRunCreditCheck();
  };

  return (
    <ApplicationJourneyStepLayout
      title="Let's run a quick credit check"
      description={
        "We'll do a soft ID check that won't affect your score. Interested funders will handle full credit checks."
      }
      secondaryAction={{
        label: 'Back',
        onClick: goPrevious,
        disabled: isBusy,
      }}
      primaryAction={{
        label: primaryActionLabel,
        onClick: handlePrimaryAction,
        disabled: isBusy,
        loading: isBusy,
      }}
    >
      {!application ? (
        <div className="application-card">
          <div className="application-empty-state">
            <h3>We couldn’t find your funding profile</h3>
            <p>
              Please refresh the page to reload your application data, then try
              running the credit check again.
            </p>
          </div>
        </div>
      ) : (
        <div className="application-card credit-step">
          <ErrorBanner message={errorMessage} />

          <div className="credit-step__card">
            <FormField label="ID number" as="div" htmlFor="credit-id-number">
              <TextInput
                id="credit-id-number"
                name="credit-id-number"
                type="text"
                autoComplete="off"
                inputMode="numeric"
                value={idNumber}
                onChange={(event) => {
                  setIdNumber(event.target.value);
                  if (hasCompletedCreditCheck) {
                    setHasCompletedCreditCheck(false);
                  }
                  if (errorMessage) {
                    setErrorMessage(null);
                  }
                }}
                disabled={isBusy}
                aria-required="true"
                aria-describedby="credit-disclaimer"
              />
            </FormField>
            <p className="credit-step__disclaimer" id="credit-disclaimer">
              Disclaimer: You consent to running this credit check on your ID
              number and on behalf of the company and directors.
            </p>
          </div>

          {shouldShowScoreCard ? (
            <div className="credit-score-card">
              <header>
                <p>Your Credit Score</p>
                <strong>{creditScoreCard.displayScore}</strong>
                <span>{creditScoreCard.label}</span>
              </header>

              <div className="credit-score-card__bar">
                <div className="credit-score-card__gradient" />
                <div
                  className="credit-score-card__indicator"
                  style={{left: creditScoreCard.indicatorPosition}}
                />
              </div>

              <p className="credit-score-card__message">
                {creditScoreCard.message}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </ApplicationJourneyStepLayout>
  );
};

export default ApplicationCreditCheckStep;
